/**
 * RC7 · M1 · Mobile authentication storage abstraction.
 *
 * Web: uses `localStorage` (existing traveller Web behaviour — unchanged).
 *
 * Native (RATIFIED · 2026-08-10):
 *   uses `@aparajita/capacitor-secure-storage@^7.1.6` — Capacitor 7 track.
 *     iOS      : system Keychain (encrypted at rest with the hardware
 *                secure enclave on modern devices).
 *     Android  : Android Keystore-generated AES-GCM key + AES-GCM-encrypted
 *                blob stored in SharedPreferences (app-specific; deleted
 *                on app uninstall).
 *
 *   The plugin choice is documented in:
 *     /app/docs/governance/audits/rc7-m1-secure-storage/
 *     RC7-M1-SECURE-STORAGE-REVIEW.md
 *
 *   Biometric document reveal is a SEPARATE architecture (M1-G) and is
 *   NOT provided by this plugin. Do not conflate credential storage with
 *   biometric unlock.
 *
 * Contract:
 *   - `getItem(key)`        → Promise<string | null>
 *   - `setItem(key, value)` → Promise<void>
 *   - `removeItem(key)`     → Promise<void>
 *   - `clear()`             → Promise<void>
 *
 * All auth token reads/writes MUST go through this module. Direct
 * `localStorage.setItem("token", ...)` in traveller code is a
 * violation of the M1 architecture.
 */
import { SecureStorage } from "@aparajita/capacitor-secure-storage";
import { isNativePlatform } from "@/lib/platform";

const isNative = () => isNativePlatform();

const AUTH_TOKEN_KEY = "cc.auth.token";

async function _get(key) {
  if (isNative()) {
    // SecureStorage.get returns the previously-stored JSON value, or
    // null if no entry exists. We store string tokens verbatim.
    const value = await SecureStorage.get(key);
    return value == null ? null : String(value);
  }
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

async function _set(key, value) {
  if (isNative()) {
    await SecureStorage.set(key, value);
    return;
  }
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* quota / private-mode — swallow, caller can re-attempt */
  }
}

async function _remove(key) {
  if (isNative()) {
    await SecureStorage.remove(key);
    return;
  }
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

async function _clear() {
  if (isNative()) {
    await SecureStorage.clear();
    return;
  }
  try {
    window.localStorage.clear();
  } catch {
    /* ignore */
  }
}

export const secureAuthStorage = {
  getItem: _get,
  setItem: _set,
  removeItem: _remove,
  clear: _clear,

  // Convenience helpers around the canonical auth-token key.
  getAuthToken: () => _get(AUTH_TOKEN_KEY),
  setAuthToken: (token) => _set(AUTH_TOKEN_KEY, token),
  clearAuthToken: () => _remove(AUTH_TOKEN_KEY),

  AUTH_TOKEN_KEY,
};

export default secureAuthStorage;
